import { useEffect, useRef, useState } from 'react';
import { CalendarClockIcon, SendIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { useAdmin } from '../AdminContext';
import { scheduleNewsletter, sendNewsletter, sendNewsletterTest } from '../adminApi';
import { issueState } from './newsletterRules';
import { ApiError, newRequestId } from '../../core/api/http';
import { errorMessage } from '../../core/errors';
import { euro } from '../../core/format/currency';
import { fromIso, longDate, nowStamp } from '../../core/format/dates';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import type { NewsletterIssue } from '../../core/types/newsletter';
import type { Segment } from '../../core/types/mail';
type Props = {
  issue: NewsletterIssue;
  /** Rode controles: dan kan hij niet de deur uit. */
  errors: number;
  segments: Segment[] | null;
  totalReaders: number | null;
};
type Action = 'test' | 'send' | 'schedule' | 'unschedule';
const pct = (part: number, whole: number) => whole ? `${Math.round(part / whole * 100)}%` : '–';

/**
 * Versturen vanuit het beheer (POST /admin/newsletters/{id}/…). Nooit optimistisch: de stand
 * verandert pas na het antwoord. Eén requestId per poging, ook bij opnieuw proberen na een
 * netwerkfout, zodat een nieuwsbrief nooit twee keer gaat.
 */
export function NewsletterSendPanel({
  issue,
  errors,
  segments,
  totalReaders
}: Props) {
  const {
    changed,
    saving,
    applyDelivery,
    setMarketing
  } = useAdmin();
  const [busy, setBusy] = useState<Action | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [sendAt, setSendAt] = useState(() => issue.delivery?.scheduledAt ?? (issue.sendDate ? `${issue.sendDate}T09:00` : ''));
  const attempts = useRef<Partial<Record<Action, string>>>({});
  const state = issueState(issue);
  const unsaved = changed.newsletters;
  const segment = segments?.find(s => s.id === issue.segmentId);
  // Verwijderde doelgroep: niet stil naar iedereen sturen, maar laten kiezen.
  const segmentGone = Boolean(issue.segmentId && segments && !segment);
  const readers = segment ? segment.count : totalReaders;
  const blockedReason = unsaved ? 'Sla eerst op: de server verstuurt de opgeslagen versie.' : segmentGone ? 'De gekozen doelgroep bestaat niet meer. Kies een andere.' : errors > 0 ? 'Los eerst de rode punten op.' : null;
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);
  const run = async (action: Action, call: (requestId: string) => Promise<unknown>) => {
    if (busy) return;
    const requestId = attempts.current[action] ??= newRequestId();
    setBusy(action);
    try {
      await call(requestId);
      delete attempts.current[action];
      return true;
    } catch (err) {
      // Netwerkfout: dezelfde id bewaren, zodat opnieuw proberen niet dubbel verstuurt.
      if (err instanceof ApiError) delete attempts.current[action];
      toast.error('Dat lukte niet', {
        description: errorMessage(err) ?? 'Probeer het zo nog eens. Er is niets dubbel verstuurd.'
      });
      return false;
    } finally {
      setBusy(null);
    }
  };
  const test = () => run('test', async requestId => {
    await sendNewsletterTest(issue.id, {
      requestId
    });
    toast.success('Testmail verstuurd', {
      description: 'Naar je eigen adres. Bekijk hem op je telefoon.'
    });
  });
  const send = () => run('send', async requestId => {
    const res = await sendNewsletter(issue.id, {
      requestId
    });
    applyDelivery(res.id, res.delivery);
    setConfirming(false);
    toast.success('Nieuwsbrief verstuurd', {
      description: `Naar ${res.delivery?.stats?.recipients ?? 'je'} lezers. De cijfers komen de komende dagen binnen.`
    });
  });
  const schedule = (at: string | null) => run(at ? 'schedule' : 'unschedule', async requestId => {
    const res = await scheduleNewsletter(issue.id, {
      requestId,
      sendAt: at
    });
    applyDelivery(res.id, res.delivery);
    toast.success(at ? 'Ingepland' : 'Inplannen geannuleerd', {
      description: at ? `Gaat ${longDate(fromIso(at.slice(0, 10)))} om ${at.slice(11, 16)}.` : 'Hij staat weer als concept klaar.'
    });
  });
  if (state === 'verzonden') {
    const s = issue.delivery?.stats;
    return <Panel title="Verzonden" description={issue.delivery ? `${issue.delivery.segmentName}${issue.delivery.sentAt ? ` · ${longDate(fromIso(issue.delivery.sentAt.slice(0, 10)))}` : ''}` : undefined} data-id="element-3323" data-mp="jjar4xhq4bv4">
        {s ? <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-id="element-3324" data-mp="17f5f2q9c2v6m">
            {[['Ontvangers', String(s.recipients)], ['Geopend', pct(s.opened, s.recipients)], ['Geklikt', pct(s.clicked, s.recipients)], ['Afgemeld', String(s.unsubscribed)], ['Bestellingen', String(s.orders)], ['Omzet', euro(s.revenueCents / 100)]].map(([label, value]) => <div key={label} data-id="element-3325" data-mp="22h619nh3vvbe">
                <dt className="text-[12px] text-muted" data-id="element-3326" data-mp="22hz1s6ph1ol3">{label}</dt>
                <dd className="text-[18px] tabular-nums text-ink" data-id="element-3327" data-mp="27fqq6qqwqskj">{value}</dd>
              </div>)}
          </dl> : <p className="text-[13px] text-muted" data-id="element-3328" data-mp="2cgesi8kl6vu2">Je hebt hem zelf als verstuurd gemarkeerd (via Brevo). Daarvan zijn hier geen cijfers.</p>}
        {s && <p className="mt-4 text-[12px] leading-relaxed text-muted" data-id="element-3329" data-mp="2ldp640kvmx25">Omzet = betaalde bestellingen binnen 7 dagen na een klik. Openen telt niet altijd mee: sommige mailapps laden geen afbeeldingen.</p>}
      </Panel>;
  }
  return <Panel title="Versturen" description={readers === null ? undefined : `Naar ${readers} ${readers === 1 ? 'lezer' : 'lezers'}.`} data-id="element-3330" data-mp="2v6mqyddccy3x">
      <div className="space-y-5" data-id="element-3331" data-mp="352zjv912b7xm">
        <Field label="Doelgroep" hint="Alleen bevestigde lezers. Groepen stel je samen bij Abonnees & doelgroepen." data-id="element-3332" data-mp="3540u5ba97qea">
          <select value={issue.segmentId ?? ''} disabled={state === 'ingepland'} onChange={e => setMarketing(m => ({
          ...m,
          newsletters: m.newsletters.map(n => n.id === issue.id ? {
            ...n,
            segmentId: e.target.value || null
          } : n)
        }))} className={inputCls} data-id="element-3333" data-mp="394kqwdqx0rzw">
            {segmentGone && <option value={issue.segmentId ?? ''} data-id="element-3334" data-mp="1e0wesivrex28">Verwijderde doelgroep: kies een andere</option>}
            <option value="" data-id="element-3335" data-mp="1sp9fft5sl432">Alle abonnees{totalReaders !== null ? ` (${totalReaders})` : ''}</option>
            {segments?.map(s => <option key={s.id} value={s.id} data-id="element-3336" data-mp="1nqpunw2xpyca">
                {s.name} ({s.count})
              </option>)}
          </select>
        </Field>

        {state === 'ingepland' && issue.delivery?.scheduledAt ? <div className="rounded-md bg-sand px-4 py-3" data-id="element-3337" data-mp="7cpv4m804apk">
            <p className="text-[14px] text-ink" data-id="element-3338" data-mp="7d80q9gk01a5">
              Ingepland voor {longDate(fromIso(issue.delivery.scheduledAt.slice(0, 10)))} om {issue.delivery.scheduledAt.slice(11, 16)}
            </p>
            <p className="mt-0.5 text-[12px] text-muted" data-id="element-3339" data-mp="3pd3ma0bdomwa">Wil je nog iets aanpassen? Annuleer het inplannen, pas aan, sla op en plan opnieuw.</p>
            <button type="button" onClick={() => schedule(null)} disabled={busy !== null} className={`${btnSecondary} mt-3`} data-id="element-3340" data-mp="3fms4payruf6x">
              {busy === 'unschedule' ? 'Bezig…' : 'Inplannen annuleren'}
            </button>
          </div> : <>
            <div className="flex flex-wrap gap-2" data-id="element-3341" data-mp="35n9mayyif1gz">
              <button type="button" onClick={test} disabled={busy !== null || unsaved} aria-describedby={unsaved ? `test-${issue.id}` : undefined} className={btnSecondary} data-id="element-3342" data-mp="3ao74vcnflxj0">
                <SendIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3343" data-mp="31ou3l79gof9d" />
                {busy === 'test' ? 'Versturen…' : 'Testmail naar mij'}
              </button>
            </div>
            {unsaved && <p id={`test-${issue.id}`} className="-mt-3 text-[12px] text-muted" data-id="element-3344" data-mp="28gl7dxuzu82n">
                Sla eerst op: de testmail gebruikt de opgeslagen versie.
              </p>}

            <Field label="Inplannen op" hint="Dinsdag- of donderdagochtend wordt het best gelezen." data-id="element-3345" data-mp="1yq8m23fcuavg">
              <div className="flex flex-wrap gap-2" data-id="element-3346" data-mp="1uowvnvonplm7">
                <input type="datetime-local" value={sendAt} min={nowStamp()} onChange={e => setSendAt(e.target.value)} className={`${inputCls} sm:max-w-[240px]`} data-id="element-3347" data-mp="1uo5a7dorx49n" />
                <button type="button" onClick={() => schedule(sendAt)} disabled={busy !== null || Boolean(blockedReason) || !sendAt || sendAt <= nowStamp()} className={btnSecondary} data-id="element-3348" data-mp="1fsflv8w48f1r">
                  <CalendarClockIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3349" data-mp="3643g58onna6n" />
                  {busy === 'schedule' ? 'Bezig…' : 'Inplannen'}
                </button>
              </div>
            </Field>

            <div className="border-t border-line pt-4" data-id="element-3350" data-mp="wu40v7hil7ib">
              {confirming ? <div role="alertdialog" aria-labelledby={`verstuur-${issue.id}`} className="rounded-md bg-blush/40 px-4 py-3" data-id="element-3351" data-mp="11lm27wng9a3t">
                  <p id={`verstuur-${issue.id}`} className="text-[14px] text-ink" data-id="element-3352" data-mp="15m6237zeqrtn">
                    Nu versturen naar {readers ?? 'alle'} {readers === 1 ? 'lezer' : 'lezers'}? Dit kan niet terug.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-3353" data-mp="dydwxeq0u11s">
                    <button ref={confirmRef} type="button" onClick={send} disabled={busy !== null} className={btnPrimary} data-id="element-3354" data-mp="hwtqy2kis9op">
                      {busy === 'send' ? 'Versturen…' : 'Ja, verstuur'}
                    </button>
                    <button type="button" onClick={() => setConfirming(false)} disabled={busy === 'send'} className={btnSecondary} data-id="element-3355" data-mp="1tcs70qe47vzf">
                      Niet versturen
                    </button>
                  </div>
                </div> : <button type="button" onClick={() => setConfirming(true)} disabled={busy !== null || saving || Boolean(blockedReason)} aria-describedby={blockedReason ? `blokkade-${issue.id}` : undefined} className={btnPrimary} data-id="element-3356" data-mp="2p02qb7wyr8qd">
                  <SendIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3357" data-mp="214q7z2dwofzg" />
                  Nu versturen
                </button>}
              {blockedReason && <p id={`blokkade-${issue.id}`} className="mt-2 text-[12px] text-muted" data-id="element-3358" data-mp="2azi7wcuynexe">
                  {blockedReason}
                </p>}
            </div>
          </>}
      </div>
    </Panel>;
}